import { alpha, createTheme, lighten, darken } from '@mui/material';
import { themeColors, lightPalette, commonComponents, gradients, shadows } from '../colors';

const colors = {
  gradients: gradients,
  shadows: shadows,
  alpha: {
    white: {
      5: alpha(themeColors.white, 0.02),
      10: alpha(themeColors.white, 0.1),
      30: alpha(themeColors.white, 0.3),
      50: alpha(themeColors.white, 0.5),
      70: alpha(themeColors.white, 0.7),
      100: themeColors.white
    },
    trueWhite: {
      5: alpha(themeColors.white, 0.02),
      10: alpha(themeColors.white, 0.1),
      30: alpha(themeColors.white, 0.3),
      50: alpha(themeColors.white, 0.5),
      70: alpha(themeColors.white, 0.7),
      100: themeColors.white
    },
    black: {
      5: alpha(themeColors.black, 0.02),
      10: alpha(themeColors.black, 0.1),
      30: alpha(themeColors.black, 0.3),
      50: alpha(themeColors.black, 0.5),
      70: alpha(themeColors.black, 0.7),
      100: themeColors.black
    }
  },
  secondary: {
    lighter: alpha(themeColors.secondary, 0.1),
    light: lighten(themeColors.secondary, 0.25),
    main: themeColors.secondary,
    dark: darken(themeColors.secondary, 0.2)
  },
  primary: {
    lighter: alpha(themeColors.primary, 0.1),
    light: lighten(themeColors.primary, 0.3),
    main: themeColors.primary,
    dark: darken(themeColors.primary, 0.2)
  },
  success: {
    lighter: alpha(themeColors.success, 0.1),
    light: lighten(themeColors.success, 0.3),
    main: themeColors.success,
    dark: darken(themeColors.success, 0.2)
  },
  warning: {
    lighter: alpha(themeColors.warning, 0.1),
    light: lighten(themeColors.warning, 0.3),
    main: themeColors.warning,
    dark: darken(themeColors.warning, 0.2)
  },
  error: {
    lighter: alpha(themeColors.error, 0.1),
    light: lighten(themeColors.error, 0.3),
    main: themeColors.error,
    dark: darken(themeColors.error, 0.2)
  },
  info: {
    lighter: alpha(themeColors.info, 0.1),
    light: lighten(themeColors.info, 0.3),
    main: themeColors.info,
    dark: darken(themeColors.info, 0.2)
  }
};

export const PureLightTheme = createTheme({
  colors: {
    gradients: colors.gradients,
    shadows: colors.shadows,
    alpha: colors.alpha,
    secondary: colors.secondary,
    primary: colors.primary,
    success: colors.success,
    warning: colors.warning,
    error: colors.error,
    info: colors.info
  },
  general: {
    reactFrameworkColor: '#00D8FF',
    borderRadiusSm: '6px',
    borderRadius: '10px',
    borderRadiusLg: '12px',
    borderRadiusXl: '16px'
  },
  sidebar: {
    background: colors.alpha.white[100],
    dividerBg: '#f2f5f9',
    menuItemColor: '#242E6F',
    menuItemColorActive: colors.primary.main,
    menuItemBg: colors.alpha.white[100],
    menuItemBgActive: '#f2f5f9',
    menuItemIconColor: lighten(colors.secondary.main, 0.3),
    menuItemIconColorActive: colors.primary.main,
    menuItemHeadingColor: darken(colors.secondary.main, 0.3),
    boxShadow: '2px 0 3px rgba(159, 162, 191, .18), 1px 0 1px rgba(159, 162, 191, 0.32)',
    width: '240px'
  },
  header: {
    height: '80px',
    background: colors.alpha.white[100],
    boxShadow: colors.shadows.cardSm
  },
  spacing: 9,
  palette: lightPalette,
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1840
    }
  },
  components: {
    ...commonComponents,
    MuiCssBaseline: {
      styleOverrides: {
        ...commonComponents.MuiCssBaseline.styleOverrides,
        body: {
          ...commonComponents.MuiCssBaseline.styleOverrides.body,
          backgroundColor: lightPalette.background.main,
          color: lightPalette.text.primary
        },
        '@keyframes pulse': {
          '0%': { transform: 'scale(.75)' },
          '20%': { transform: 'scale(1.1)' },
          '40%': { transform: 'scale(.75)' },
          '60%': { transform: 'scale(1.05)' },
          '80%': { transform: 'scale(.75)' },
          '100%': { transform: 'scale(.75)' }
        },
        '@keyframes ripple': {
          '0%': { transform: 'scale(.8)', opacity: 1 },
          '100%': { transform: 'scale(2.8)', opacity: 0 }
        },
        '@keyframes float': {
          '0%': { transform: 'translate(0%, 0%)' },
          '100%': { transform: 'translate(3%, 3%)' }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        ...commonComponents.MuiPaper.styleOverrides,
        root: {
          ...commonComponents.MuiPaper.styleOverrides.root,
          backgroundColor: lightPalette.background.paper,
          backgroundImage: 'none',
          borderRadius: 0,
          border: '1px solid rgba(0, 0, 0, 0.04)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)'
        },
        elevation0: { boxShadow: 'none' },
        elevation: { boxShadow: colors.shadows.card },
        elevation2: { boxShadow: colors.shadows.cardSm },
        elevation24: { boxShadow: colors.shadows.cardLg },
        outlined: { boxShadow: colors.shadows.card }
      }
    },
    MuiCard: {
      defaultProps: {
        elevation: 0
      },
      styleOverrides: {
        root: {
          backgroundColor: lightPalette.background.paper,
          backgroundImage: 'none',
          borderRadius: 16,
          border: '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.04)',
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.08)',
            borderColor: 'rgba(0, 0, 0, 0.12)',
            transform: 'translateY(-2px)'
          }
        }
      }
    },
    MuiCardHeader: {
      styleOverrides: {
        ...commonComponents.MuiCardHeader.styleOverrides,
        root: {
          borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
          padding: '24px 24px 16px'
        }
      }
    },
    MuiCardContent: {
      styleOverrides: commonComponents.MuiCardContent.styleOverrides
    },
    MuiListItemButton: {
      styleOverrides: {
        ...commonComponents.MuiListItemButton.styleOverrides,
        root: {
          ...commonComponents.MuiListItemButton.styleOverrides.root,
          '&:hover': {
            backgroundColor: lightPalette.action.hover
          },
          '&.Mui-selected': {
            backgroundColor: alpha(lightPalette.primary.main, 0.08),
            '&:hover': {
              backgroundColor: alpha(lightPalette.primary.main, 0.12)
            }
          }
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        ...commonComponents.MuiDivider.styleOverrides,
        root: {
          ...commonComponents.MuiDivider.styleOverrides.root,
          background: alpha(lightPalette.common.black, 0.1)
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: lightPalette.customPrimary.main,
          color: lightPalette.common.white,
          backgroundImage: 'none',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: lightPalette.background.paper,
          color: lightPalette.text.primary,
          borderRight: '1px solid rgba(0, 0, 0, 0.08)'
        }
      }
    },
    MuiButton: {
      ...commonComponents.MuiButton,
      styleOverrides: {
        ...commonComponents.MuiButton.styleOverrides,
        contained: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
          }
        },
        containedSecondary: {
          backgroundColor: colors.secondary.main,
          color: colors.alpha.white[100],
          border: `1px solid ${alpha(lightPalette.common.black, 0.3)}`
        },
        outlinedSecondary: {
          backgroundColor: colors.alpha.white[100],
          '&:hover, &.MuiSelected': {
            backgroundColor: alpha(lightPalette.common.black, 0.05),
            color: colors.alpha.black[100]
          }
        }
      }
    },
    // Add other component overrides from your original file
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          marginLeft: 8,
          marginRight: 8,
          fontWeight: 'bold'
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiInputAdornment-positionEnd.MuiInputAdornment-outlined': {
            paddingRight: 6
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(lightPalette.common.black, 0.5)
          },
          '&.Mui-focused:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: lightPalette.primary.main
          }
        }
      }
    },
    MuiListSubheader: {
      styleOverrides: {
        colorPrimary: {
          fontWeight: 'bold',
          lineHeight: '40px',
          fontSize: 13,
          background: alpha(lightPalette.common.black, 0.05),
          color: alpha(lightPalette.common.black, 0.7)
        }
      }
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          borderRadius: '50px'
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        colorSecondary: {
          background: alpha(lightPalette.common.black, 0.05),
          color: lightPalette.common.black,
          '&:hover': {
            background: alpha(lightPalette.common.black, 0.1)
          }
        },
        deleteIcon: {
          color: lighten(lightPalette.error.main, 0.3),
          '&:hover': {
            color: lightPalette.error.main
          }
        }
      }
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          '&.Mui-expanded': { margin: 0 },
          '&::before': { display: 'none' }
        }
      }
    },
    MuiAvatarGroup: {
      styleOverrides: {
        root: { alignItems: 'center' },
        avatar: {
          background: alpha(lightPalette.common.black, 0.1),
          fontSize: 13,
          color: alpha(lightPalette.common.black, 0.7),
          fontWeight: 'bold',
          '&:first-of-type': { border: 0, background: 'transparent' }
        }
      }
    },
    MuiPaginationItem: {
      styleOverrides: {
        page: {
          fontSize: 13,
          fontWeight: 'bold',
          transition: 'all .2s'
        },
        textPrimary: {
          '&.Mui-selected': { boxShadow: colors.shadows.primary },
          '&.MuiButtonBase-root:hover': { background: alpha(lightPalette.common.black, 0.05) },
          '&.Mui-selected.MuiButtonBase-root:hover': { background: lightPalette.primary.main }
        }
      }
    },
    MuiToggleButton: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          color: lightPalette.primary.main,
          background: lightPalette.common.white,
          transition: 'all .2s',
          '&:hover, &.Mui-selected, &.Mui-selected:hover': {
            color: lightPalette.common.white,
            background: lightPalette.primary.main
          }
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: 8,
          '& .MuiTouchRipple-root': { borderRadius: 8 }
        },
        sizeSmall: { padding: 4 }
      }
    },
    MuiListItemText: {
      styleOverrides: {
        root: { margin: 0 }
      }
    },
    MuiLink: {
      defaultProps: { underline: 'hover' }
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          '& .MuiSlider-valueLabelCircle, .MuiSlider-valueLabelLabel': { transform: 'none' },
          '& .MuiSlider-valueLabel': {
            borderRadius: 6,
            background: lightPalette.common.black,
            color: lightPalette.common.white
          }
        }
      }
    },
    MuiList: {
      styleOverrides: {
        root: {
          padding: 0,
          '& .MuiListItem-button': {
            transition: 'all .2s',
            '& > .MuiSvgIcon-root': { minWidth: 34 },
            '& .MuiTouchRipple-root': { opacity: 0.2 }
          },
          '& .MuiListItem-root.MuiButtonBase-root.Mui-selected': {
            backgroundColor: alpha(lightPalette.primary.lighter, 0.4)
          },
          '& .MuiMenuItem-root.MuiButtonBase-root:active': {
            backgroundColor: alpha(lightPalette.primary.lighter, 0.4)
          }
        },
        padding: {
          padding: '6px',
          '& .MuiListItem-button': {
            borderRadius: 6,
            margin: '1px 0'
          }
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        root: { height: 38, minHeight: 38, overflow: 'visible' },
        indicator: {
          height: 38,
          minHeight: 38,
          borderRadius: 6,
          border: `1px solid ${lightPalette.primary.dark}`,
          boxShadow: `0px 2px 10px ${lightPalette.primary.light}`
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          padding: 0,
          height: 38,
          minHeight: 38,
          borderRadius: 6,
          transition: 'color .2s',
          textTransform: 'capitalize',
          '&.MuiButtonBase-root': {
            minWidth: 'auto',
            paddingLeft: 20,
            paddingRight: 20,
            marginRight: 4
          },
          '&.Mui-selected, &.Mui-selected:hover': {
            color: lightPalette.common.white,
            zIndex: 5
          },
          '&:hover': { color: lightPalette.common.black }
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          background: 'transparent',
          transition: 'all .2s',
          '&:hover, &:active, &.active, &.Mui-selected': {
            color: lightPalette.common.black,
            background: alpha(lightPalette.primary.lighter, 0.4)
          }
        }
      }
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&.MuiButtonBase-root': {
            color: lightPalette.secondary.main,
            '&:hover, &:active, &.active, &.Mui-selected': {
              color: lightPalette.common.black,
              background: lighten(lightPalette.primary.lighter, 0.5)
            }
          }
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        tag: { margin: 1 },
        root: {
          '.MuiAutocomplete-inputRoot.MuiOutlinedInput-root .MuiAutocomplete-endAdornment': {
            right: 14
          }
        },
        clearIndicator: {
          background: lightPalette.error.lighter,
          color: lightPalette.error.main,
          marginRight: 8,
          '&:hover': {
            background: lightPalette.error.lighter,
            color: lightPalette.error.dark
          }
        },
        popupIndicator: {
          color: alpha(lightPalette.common.black, 0.5),
          '&:hover': {
            background: lightPalette.primary.lighter,
            color: lightPalette.primary.main
          }
        }
      }
    },
    MuiTablePagination: {
      styleOverrides: {
        toolbar: {
          '& .MuiIconButton-root': { padding: 8 }
        },
        select: {
          '&:focus': { backgroundColor: 'transparent' }
        }
      }
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '0 !important',
          padding: '0 !important'
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        head: { background: alpha(lightPalette.common.black, 0.05) },
        root: {
          transition: 'background-color .2s',
          '&.MuiTableRow-hover:hover': { backgroundColor: alpha(lightPalette.common.black, 0.05) }
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        message: { lineHeight: 1.5, fontSize: 14 },
        standardInfo: { color: lightPalette.info.main },
        action: { color: alpha(lightPalette.common.black, 0.7) }
      }
    },
    MuiTimelineDot: {
      styleOverrides: {
        root: {
          margin: 0,
          zIndex: 5,
          position: 'absolute',
          top: '50%',
          marginTop: -6,
          left: -6
        },
        outlined: {
          backgroundColor: lightPalette.common.white,
          boxShadow: `0 0 0 6px ${lightPalette.common.white}`
        }
      }
    },
    MuiTimelineConnector: {
      styleOverrides: {
        root: {
          position: 'absolute',
          height: '100%',
          top: 0,
          borderRadius: 50,
          backgroundColor: alpha(lightPalette.common.black, 0.1)
        }
      }
    },
    MuiTimelineItem: {
      styleOverrides: {
        root: {
          minHeight: 0,
          padding: '8px 0',
          '&:before': { display: 'none' }
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: alpha(lightPalette.common.black, 0.95),
          padding: '8px 16px',
          fontSize: 13
        },
        arrow: {
          color: alpha(lightPalette.common.black, 0.95)
        }
      }
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          height: 33,
          overflow: 'visible',
          '& .MuiButtonBase-root': {
            position: 'absolute',
            padding: 6,
            transition: 'left 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms'
          },
          '& .MuiIconButton-root': { borderRadius: 100 },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { opacity: 0.3 }
        },
        thumb: {
          border: `1px solid ${alpha(lightPalette.common.black, 0.3)}`,
          boxShadow: `0px 9px 14px ${alpha(lightPalette.common.black, 0.1)}, 0px 2px 2px ${alpha(lightPalette.common.black, 0.1)}`
        },
        track: {
          backgroundColor: alpha(lightPalette.common.black, 0.05),
          border: `1px solid ${alpha(lightPalette.common.black, 0.1)}`,
          boxShadow: `inset 0px 1px 1px ${alpha(lightPalette.common.black, 0.1)}`,
          opacity: 1
        },
        colorPrimary: {
          '& .MuiSwitch-thumb': { backgroundColor: lightPalette.common.white },
          '&.Mui-checked .MuiSwitch-thumb': { backgroundColor: lightPalette.primary.main }
        }
      }
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          paddingTop: 20,
          paddingBottom: 20,
          background: alpha(lightPalette.common.black, 0.05)
        }
      }
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          '&.MuiStepIcon-completed': { color: lightPalette.success.main }
        }
      }
    }
  },
  shape: {
    borderRadius: 10
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
    h1: { fontWeight: 700, fontSize: 35 },
    h2: { fontWeight: 700, fontSize: 30 },
    h3: { fontWeight: 700, fontSize: 25, lineHeight: 1.4, color: lightPalette.text.primary },
    h4: { fontWeight: 700, fontSize: 16 },
    h5: { fontWeight: 700, fontSize: 14 },
    h6: { fontSize: 15 },
    body1: { fontSize: 14 },
    body2: { fontSize: 14 },
    button: { fontWeight: 600 },
    caption: { fontSize: 13, textTransform: 'uppercase', color: alpha(lightPalette.common.black, 0.5) },
    subtitle1: { fontSize: 14, color: alpha(lightPalette.common.black, 0.7) },
    subtitle2: { fontWeight: 400, fontSize: 15, color: alpha(lightPalette.common.black, 0.7) },
    overline: { fontSize: 13, fontWeight: 700, textTransform: 'uppercase' }
  },
  shadows: [
    'none', 'none', 'none', 'none', 'none',
    'none', 'none', 'none', 'none', 'none',
    'none', 'none', 'none', 'none', 'none',
    'none', 'none', 'none', 'none', 'none',
    'none', 'none', 'none', 'none', 'none'
  ]
});